import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, History, List } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * RecordNavigator - Navigation through records
 *
 * Layout: [‹] [›] Name …                    [N] von Total [Liste]
 * Name und Positionsanzeige sind immer gleichzeitig sichtbar.
 * Das Nummernfeld = „gehe zu Datensatz xy“ (Enter / Blur).
 *
 * @param {number} currentIndex - Current 0-based index
 * @param {number} totalCount - Total number of records
 * @param {function} onNavigate - Callback when navigating to a new index (receives 0-based index)
 * @param {string} className - Additional CSS classes
 * @param {boolean} darkMode - Use dark mode styling (for dark backgrounds)
 * @param {array} records - Optional array of records for the list dropdown
 * @param {function} getRecordLabel - Function to get display label for a record (receives record, index)
 * @param {boolean} historyMode - If true, shows history of viewed records instead of all records
 * @param {function} onHistorySelect - Callback when selecting from history (receives record)
 */
export default function RecordNavigator({
  currentIndex,
  totalCount,
  onNavigate,
  className = '',
  darkMode = false,
  records = [],
  getRecordLabel = (record, index) => `Datensatz ${index + 1}`,
  historyMode = false,
  onHistorySelect = null,
}) {
  const [showList, setShowList] = useState(false);
  const [inputValue, setInputValue] = useState(String(currentIndex + 1));
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listContainerRef = useRef(null);

  useEffect(() => {
    setInputValue(String(Math.max(0, currentIndex) + 1));
  }, [currentIndex, totalCount]);

  useEffect(() => {
    if (showList && listRef.current) {
      const selectedItem = listRef.current.querySelector('[data-selected="true"]');
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'center' });
      }
    }
  }, [showList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (listContainerRef.current && !listContainerRef.current.contains(event.target)) {
        setShowList(false);
      }
    };

    if (showList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showList]);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < totalCount - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setInputValue(value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitJump();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setInputValue(String(currentIndex + 1));
      inputRef.current?.blur();
    }
  };

  const handleInputBlur = () => {
    submitJump();
  };

  const submitJump = () => {
    const targetNumber = parseInt(inputValue, 10);

    if (!isNaN(targetNumber) && targetNumber >= 1 && targetNumber <= totalCount) {
      if (targetNumber - 1 !== currentIndex) {
        onNavigate(targetNumber - 1);
      }
      setInputValue(String(targetNumber));
    } else {
      setInputValue(String(currentIndex + 1));
    }
  };

  const handleListItemClick = (index) => {
    onNavigate(index);
    setShowList(false);
  };

  const buttonClasses = cn(
    'p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed',
    darkMode ? 'text-white hover:bg-white/20' : 'hover:bg-slate-100'
  );

  const currentLabel =
    records[currentIndex] != null
      ? getRecordLabel(records[currentIndex], currentIndex)
      : '';

  return (
    <div className={cn('flex items-center gap-1 min-w-0', className)} data-testid="record-navigator">
      <button
        onClick={goToPrevious}
        disabled={currentIndex === 0 || totalCount === 0}
        className={cn(buttonClasses, 'shrink-0')}
        title="Vorheriger Datensatz"
        data-testid="record-prev"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <button
        onClick={goToNext}
        disabled={currentIndex >= totalCount - 1 || totalCount === 0}
        className={cn(buttonClasses, 'shrink-0')}
        title="Nächster Datensatz"
        data-testid="record-next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      <span
        className={cn(
          'ml-1 min-w-[8rem] flex-1 truncate text-sm font-medium px-1.5 py-0.5',
          darkMode ? 'text-white' : 'text-slate-800',
          !currentLabel && 'invisible'
        )}
        title={currentLabel || undefined}
        data-testid="record-current-label"
      >
        {currentLabel || '\u00a0'}
      </span>

      {/* Rechts: gehe zu Datensatz xy */}
      <div className="flex items-center gap-1 shrink-0 ml-2">
        <Input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={totalCount === 0 ? '' : inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          onFocus={(e) => e.target.select()}
          disabled={totalCount === 0}
          className={cn(
            'w-14 h-7 text-sm text-center font-medium px-1',
            darkMode && 'bg-white text-slate-900'
          )}
          title="Gehe zu Datensatz …"
          aria-label="Gehe zu Datensatz"
          data-testid="record-jump-input"
        />
        <span className={cn('text-sm whitespace-nowrap', darkMode ? 'text-white/70' : 'text-slate-500')}>
          von {totalCount}
        </span>

        <div className="relative" ref={listContainerRef}>
          <button
            onClick={() => setShowList(!showList)}
            disabled={historyMode ? records.length === 0 : totalCount === 0}
            className={cn(
              'ml-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors disabled:opacity-30',
              darkMode
                ? 'border-white/50 text-white hover:bg-white/20 hover:border-white'
                : 'border-slate-300 text-slate-500 hover:bg-slate-100 hover:border-slate-400',
              showList && (darkMode ? 'bg-white/20 border-white' : 'bg-slate-100 border-slate-400')
            )}
            title={historyMode ? 'Verlauf anzeigen' : 'Alle Datensätze anzeigen'}
            data-testid="show-all-records-button"
          >
            {historyMode ? <History className="w-3.5 h-3.5" /> : <List className="w-3.5 h-3.5" />}
          </button>

          {showList && records.length > 0 && (
            <div
              ref={listRef}
              className="absolute top-full right-0 mt-2 w-96 bg-white border border-slate-200 rounded-lg shadow-xl z-50"
            >
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 rounded-t-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase">
                  {historyMode ? `Verlauf (${records.length})` : `Alle Datensätze (${totalCount})`}
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {records.map((record, index) => {
                  const isSelected = historyMode ? index === 0 : index === currentIndex;
                  const label = getRecordLabel(record, index);

                  return (
                    <div
                      key={historyMode ? `history-${index}` : (record.id || index)}
                      data-selected={isSelected ? 'true' : undefined}
                      onClick={() => {
                        if (historyMode && onHistorySelect) {
                          onHistorySelect(record);
                        } else {
                          handleListItemClick(index);
                        }
                        setShowList(false);
                      }}
                      className={cn(
                        'px-3 py-2 text-sm hover:bg-blue-50 transition-colors cursor-pointer text-slate-700',
                        isSelected && 'bg-blue-100 text-blue-800 font-medium'
                      )}
                    >
                      <span className="text-slate-400 mr-1">{index + 1}.</span>
                      <span className="text-slate-800">{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showList && historyMode && records.length === 0 && (
            <div className="absolute top-full right-0 mt-2 w-72 bg-white border border-slate-200 rounded-lg shadow-xl z-50">
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                Kein Verlauf vorhanden
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
